/**
 * # Side Fixed Dock
 *
 * @example
 * new Spark.SideFixedDock(el, {sideFixedPanel: Spark.SideFixedPanel});
 *
 * @module components/side-fixed-dock.js
 * @since 3.4.0
 */

import BaseComponent from './base';

import mixin from '../helpers/util/mixin';

import events from '../mixins/events';

class SideFixedDock extends BaseComponent {

  /**
   * SideFixedDock constructor
   * @param {Element} el base DOM element
   * @param {Object} params
   * @constructor
   */
  constructor(el, params = {}) {
    super(el, params);

    if (params.sideFixedPanel) this.sideFixedPanel = params.sideFixedPanel;

    this._bindEventListenerCallbacks();
    this._addEventListeners();
  }


  /**
   * Sets up references
   * @param  {Element} el base DOM element
   * @private
   */
  _cacheElements(el) {
    this.el = el;
  }


  /**
   * Create bound versions of event listener callbacks and store them.
   * Otherwise we can't unbind from these events later because the
   * function signatures won't match.
   * @private
   */
  _bindEventListenerCallbacks() {
    this._onClickBound = this._onClick.bind(this);
  }


  /**
   * Add event listeners for DOM events.
   * @private
   */
  _addEventListeners() {
    this.el.addEventListener('click', this._onClickBound, true);
  }


  /**
   * Remove event listeners for DOM events..
   * @private
   */
  _removeEventListeners() {
    this.el.removeEventListener('click', this._onClickBound, true);
  }


  /**
   * On Click, toggle panel if target was button
   * @param {Object} e
   * @private
   */
  _onClick(e) {
    const self = this;

    if (e.target.classList.contains('spark-side-fixed-dock--toggle') && this.panels.includes(e.target.getAttribute('aria-controls'))) {

      if (e.target.getAttribute('aria-expanded') === 'true') {
        this.sideFixedPanel.close().then(() => {
          e.target.setAttribute('aria-expanded', false);
        }).catch(() => {});
      } else {
        this.clearSelected();

        if (this.panels.some(id => id === e.target.getAttribute('aria-controls')) && this.condensed) {
          const panel = this.sideFixedPanel.el.querySelector('#'.concat(e.target.getAttribute('aria-controls')));
          this.condensedPanel.el.appendChild(panel);
          panel.hidden = false;
          this.condensedPanel.open().then(() => {
            const closePanelButton = panel.querySelector('.spark-panel--fixed__close');
            if (closePanelButton) closePanelButton.focus();
            e.target.setAttribute('aria-expanded', true);
          }).catch(() => {});
        } else {
          this.panels.forEach(id => {
            self.sideFixedPanel.el.querySelector('#'.concat(id)).hidden = e.target.getAttribute('aria-controls') === id ? false : true;
          });

          this.sideFixedPanel.open().then(() => {
            const closePanelButton = document.getElementById(e.target.getAttribute('aria-controls')).querySelector('.spark-panel--fixed__close');
            if (closePanelButton) closePanelButton.focus();
            e.target.setAttribute('aria-expanded', true);
          }).catch(() => {});
        }
      }

    }
  }


  /**
   * Clear the active state of all dock buttons
   */
  clearSelected() {
    this.el.querySelectorAll('.spark-btn--icon').forEach(btn => btn.setAttribute('aria-expanded', false));
  }


  /**
   * Returns whether or not dock is currently condensed
   */
  get condensed() {
    return !!this.el.classList.contains('spark-panel-dock--condensed');
  }


  /**
   * Getter for Panels
   * @return {Arrays} all associated valid panels
   */
  get panels() {
    const panels = this.sideFixedPanel.el.querySelectorAll('.spark-panel__container__panel__content');
    let panelNames = panels.length ? Array.from(panels).map(panel => panel.id) : [];
    return panelNames.filter(name => name !== '');
  }


  /**
   * Getter for width of dock
   * @return {Number} width in pixels
   */
  get width() {
    return this.el ? this.el.scrollWidth : 0;
  }


  /**
   * Shrink dock for smaller displays
   * @public
   */
  condense() {
    if (this.sideFixedPanel.active) {
      const self = this;
      let panel = this.sideFixedPanel.el.querySelector('.spark-panel__container__panel__content:not([hidden])');
      this.condensedPanel.el.appendChild(panel);
      this.condensedPanel.open().then(() => {
        self.el.classList.add('spark-panel-dock--condensed');
        const closeButton = self.condensedPanel.el.querySelector('.spark-panel--fixed__close');
        if (closeButton) closeButton.focus();
      });
    } else {
      this.el.classList.add('spark-panel-dock--condensed');
    }
    this.el.classList.add('spark-panel-dock--condensed');
  }


  /**
   * Enlarge dock
   * @public
   */
  expand() {
    if (this.condensedPanel.active) {
      const self = this;
      const activeItem = this.el.querySelector('[aria-expanded="true"]');
      this.condensedPanel.close().then(() => {
        self.el.classList.remove('spark-panel-dock--condensed');
        if (activeItem) activeItem.focus();
        self.clearSelected();
      });
    } else {
        this.el.classList.remove('spark-panel-dock--condensed');
    }
  }
}

/**
 * Whitelisted parameters which can be set on construction.
 * @type {Array}
 */
SideFixedDock.prototype._whitelistedParams = [
  'sideFixedPanel',
  'condensedPanel'
];

mixin(SideFixedDock.prototype, events);

export default SideFixedDock;
